import React, { useState } from "react";
import PropTypes from "prop-types";
import { Plus, Trash2 } from "lucide-react";
import { IconButton } from "@mui/material";
import { getIn } from "formik";

import { clx } from "../../../../../../utils/Helpers";

import { UISelect } from "../../../../../ui";
import { UIFieldShell } from "../../../../../ui/form";
import Card from "../../../../../Card";
import AddProjectManagerModal from "../../../Modals/AddProjectManager";
import TruncatedWithTooltip from "../../../../../TruncatedWithTooltip";

import "./_ManagementClientSection.scss";

const getArrayError = (formik, name) => {
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  return touched || formik.submitCount > 0 ? error : "";
};

const getPmRowKey = (manager) =>
  String(manager.company_user_id ?? manager.id ?? "");

const PM_PLUS_ICON_SIZE = 16;

const PmPlusIcon = ({ className }) => (
  <Plus
    size={PM_PLUS_ICON_SIZE}
    strokeWidth={2}
    className={className}
    aria-hidden
  />
);

PmPlusIcon.propTypes = {
  className: PropTypes.string,
};

PmPlusIcon.defaultProps = {
  className: undefined,
};

const PortalPmClientFields = ({
  formik,
  labels,
  managerOptions,
  managerOptionsHint,
  clientOptions,
}) => {
  const [isAddPmOpen, setIsAddPmOpen] = useState(false);

  const projectManagersError = getArrayError(formik, "projectManagers");

  const addProjectManager = (manager) => {
    const companyUserId = manager.company_user_id ?? manager.id;
    const destroyed = formik.values.destroyedProjectManagers || [];
    // Re-adding a PM that was removed (but not saved) must restore the join
    // row id instead of creating a duplicate — otherwise Rails returns
    // "Company user has already been taken".
    const pendingDestroy = destroyed.find(
      (row) => String(row.company_user_id) === String(companyUserId),
    );

    formik.setFieldTouched("projectManagers", true, false);

    if (pendingDestroy) {
      formik.setFieldValue(
        "destroyedProjectManagers",
        destroyed.filter(
          (row) => String(row.company_user_id) !== String(companyUserId),
        ),
        false,
      );
    }

    formik.setFieldValue("projectManagers", [
      ...formik.values.projectManagers,
      {
        company_user_id: companyUserId,
        ...(pendingDestroy?.id != null
          ? { assignmentId: pendingDestroy.id }
          : {}),
        name: manager.name,
        email: manager.email || "",
        profile_picture_url: manager.profile_picture_url || null,
        initials: manager.initials,
        responsibility: manager.responsibility || manager.role || "",
        role: manager.responsibility || manager.role || "",
      },
    ]);
  };

  const removeProjectManager = (companyUserId) => {
    const target = formik.values.projectManagers.find(
      (manager) => getPmRowKey(manager) === String(companyUserId),
    );

    if (target?.assignmentId) {
      formik.setFieldValue(
        "destroyedProjectManagers",
        [
          ...(formik.values.destroyedProjectManagers || []),
          {
            id: target.assignmentId,
            company_user_id: target.company_user_id ?? companyUserId,
            _destroy: true,
          },
        ],
        false,
      );
    }

    formik.setFieldTouched("projectManagers", true, false);
    formik.setFieldValue(
      "projectManagers",
      formik.values.projectManagers.filter(
        (manager) => getPmRowKey(manager) !== String(companyUserId),
      ),
    );
  };

  const selectedManagerIdsForModal = formik.values.projectManagers.map((manager) =>
    getPmRowKey(manager),
  );
  const hasProjectManagers = formik.values.projectManagers.length > 0;
  const assignPmLabel = hasProjectManagers ? labels.assignMore : labels.assign;

  return (
    <>
      <div className="basic-info-divider" />

      <UIFieldShell
        label={labels.projectManagers}
        classNames={{ root: "pm-field-shell" }}
        endLabelElement={
          <button
            type="button"
            className="pm-add-button"
            disabled={managerOptions?.length === 0}
            onClick={() => setIsAddPmOpen(true)}
          >
            <PmPlusIcon className="pm-add-button__icon" />
            <span>{labels.addPm}</span>
          </button>
        }
        error={Boolean(projectManagersError)}
        helperText={projectManagersError || managerOptionsHint || ""}
      >
        <div className="pm-list">
          {formik.values.projectManagers.map((manager) => (
            <div className="pm-card" key={getPmRowKey(manager)}>
              <div className="pm-card__body">
                <TruncatedWithTooltip title={manager.name}>
                  <strong>{manager.name}</strong>
                </TruncatedWithTooltip>
                <TruncatedWithTooltip
                  title={manager.role || manager.responsibility}
                >
                  <span>{manager.role || manager.responsibility}</span>
                </TruncatedWithTooltip>
              </div>
              <IconButton
                type="button"
                className="pm-card__remove"
                aria-label={labels.removeProjectManager}
                onClick={() => removeProjectManager(getPmRowKey(manager))}
              >
                <Trash2 size={16} strokeWidth={2} aria-hidden />
              </IconButton>
            </div>
          ))}
          <button
            type="button"
            className="pm-card pm-card--assign"
            disabled={managerOptions?.length === 0}
            onClick={() => setIsAddPmOpen(true)}
          >
            <PmPlusIcon className="pm-card__plus-icon" />
            <span>{assignPmLabel}</span>
          </button>
        </div>
      </UIFieldShell>

      <div className="basic-info-divider" />

      <UISelect
        name="client.admin_id"
        label={labels.clientName}
        placeholder={labels.clientNamePlaceholder}
        options={clientOptions}
        value={
          clientOptions.find(
            (option) =>
              String(option.id) ===
              String(formik.values.client?.admin_id ?? ""),
          ) || null
        }
        onChange={(opt) => {
          formik.setFieldTouched("client.admin_id", true, false);
          formik.setFieldValue(
            "client",
            {
              id: formik.values.client?.id ?? null,
              admin_id: opt ? opt.id : null,
            },
            true,
          );
        }}
        isOptionEqualToValue={(option, selected) =>
          String(option?.id) === String(selected?.id)
        }
      />

      <AddProjectManagerModal
        open={isAddPmOpen}
        labels={labels}
        managers={managerOptions}
        selectedManagerIds={selectedManagerIdsForModal}
        onClose={() => setIsAddPmOpen(false)}
        onAdd={addProjectManager}
      />
    </>
  );
};

PortalPmClientFields.propTypes = {
  formik: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  managerOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  managerOptionsHint: PropTypes.string,
  clientOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

PortalPmClientFields.defaultProps = {
  managerOptionsHint: "",
};

const ManagementClientSection = ({
  formik,
  labels,
  hasError,
  managerOptions,
  managerOptionsHint,
  clientOptions,
}) => {
  const verificationOptions = [
    { value: "basic_verification", label: labels.basicVerification },
    { value: "supervisor_approval", label: labels.supervisorApproval },
    { value: "scanning_organizer_qr", label: labels.organizerScanning },
    { value: "scanning_supervisor_qr", label: labels.supervisorScanning },
  ];

  return (
    <Card
      title={labels.managementClient}
      classNames={{
        root: clx("basic-info-management", { "form-section--error": hasError }),
      }}
    >
      <UISelect
        name="attendance_verification_method"
        label={labels.checkInMethod}
        options={verificationOptions}
        value={
          verificationOptions.find(
            (option) =>
              option.value === formik.values.attendance_verification_method,
          ) || null
        }
        required
        onChange={(nextValue) => {
          formik.setFieldValue(
            "attendance_verification_method",
            nextValue?.value ?? "",
          );
        }}
        isOptionEqualToValue={(option, selected) =>
          option?.value === selected?.value
        }
      />

      <PortalPmClientFields
        formik={formik}
        labels={labels}
        managerOptions={managerOptions}
        managerOptionsHint={managerOptionsHint}
        clientOptions={clientOptions}
      />
    </Card>
  );
};

ManagementClientSection.propTypes = {
  formik: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  hasError: PropTypes.bool,
  managerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      company_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
      initials: PropTypes.string,
      profile_picture_url: PropTypes.string,
      role: PropTypes.string,
    }),
  ),
  managerOptionsHint: PropTypes.string,
  clientOptions: PropTypes.arrayOf(PropTypes.object),
};

ManagementClientSection.defaultProps = {
  hasError: false,
  managerOptions: [],
  managerOptionsHint: "",
  clientOptions: [],
};

export default ManagementClientSection;
